/**
 * Phase V — Per-Conversation AI Control Header.
 *
 * Renders inside the Command Center chat header. Provides:
 *  - AI Mode dropdown (off / assist / hybrid / full_auto)
 *  - Status badge (active / paused / human_handling)
 *  - Pause countdown timer
 *  - Take Control + Resume + Pause buttons
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Bot, Pause, Play, UserCheck, ChevronDown, Loader2,
  Shield, Sparkles, PowerOff, Clock as ClockIcon,
} from "lucide-react";

const MODE_OPTIONS = [
  { key: "off",       label: "OFF",       desc: "AI disabled — humans handle everything", color: "bg-gray-200 text-gray-800 border-gray-300", Icon: PowerOff },
  { key: "assist",    label: "ASSIST",    desc: "AI suggests silently — no auto-reply",   color: "bg-blue-100 text-blue-800 border-blue-300", Icon: Sparkles },
  { key: "hybrid",    label: "HYBRID",    desc: "Auto-reply only safe intents · default", color: "bg-amber-100 text-amber-800 border-amber-300", Icon: Bot },
  { key: "full_auto", label: "FULL AUTO", desc: "AI handles end-to-end · escalate if unsure", color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Bot },
];

const STATUS_BADGE = {
  active:          { label: "AI Active",       color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Bot },
  paused:          { label: "AI Paused",       color: "bg-amber-100 text-amber-800 border-amber-300",       Icon: Pause },
  human_handling:  { label: "Human Handling",  color: "bg-rose-100 text-rose-800 border-rose-300",          Icon: UserCheck },
};

const PAUSE_OPTIONS = [
  { key: "15m",    label: "15 minutes" },
  { key: "30m",    label: "30 minutes" },
  { key: "1h",     label: "1 hour" },
  { key: "today",  label: "Until end of today" },
  { key: "manual", label: "Until I resume manually" },
];

function fmtCountdown(target) {
  if (!target) return null;
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export default function AIControlHeader({ conversationId, onChange, liveAIControl }) {
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'mode' | 'pause' | 'resume' | 'takeover'
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const modeRef = useRef(null);
  const pauseRef = useRef(null);

  const load = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/inbox/${conversationId}/ai-control`);
      setControl(r.data);
    } catch (e) {
      // Non-fatal — header just doesn't render its right side
      console.debug("ai-control load failed", e?.response?.data);
    } finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  // Live update: when an ai_control.* event arrives for THIS conversation, apply the payload or refetch.
  useEffect(() => {
    if (!liveAIControl || !conversationId) return;
    if (liveAIControl.conversation_id !== conversationId) return;
    if (liveAIControl.ai_control) {
      setControl(liveAIControl.ai_control);
      onChange?.(liveAIControl.ai_control);
    } else {
      load();
    }
  }, [liveAIControl, conversationId, load, onChange]);

  // Live countdown if paused with expiry
  useEffect(() => {
    if (control?.ai_status === "paused" && control?.ai_pause_until) {
      const tick = () => {
        const c = fmtCountdown(control.ai_pause_until);
        setCountdown(c);
        if (c === "00:00") load(); // auto-refresh; backend also auto-expires
      };
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
    setCountdown(null);
  }, [control?.ai_status, control?.ai_pause_until, load]);

  // Click-away for dropdowns
  useEffect(() => {
    const onDoc = (e) => {
      if (modeRef.current && !modeRef.current.contains(e.target)) setShowModeMenu(false);
      if (pauseRef.current && !pauseRef.current.contains(e.target)) setShowPauseMenu(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const setMode = async (mode) => {
    if (!conversationId || control?.ai_mode === mode) { setShowModeMenu(false); return; }
    setBusy("mode");
    try {
      const r = await api.patch(`/admin/inbox/${conversationId}/ai-mode`, { mode });
      setControl(r.data);
      toast.success(`AI mode → ${mode.toUpperCase()}`);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change AI mode");
    } finally { setBusy(null); setShowModeMenu(false); }
  };

  const doPause = async (duration) => {
    setBusy("pause");
    try {
      const r = await api.post(`/admin/inbox/${conversationId}/ai-pause`, { duration });
      setControl(r.data);
      toast.success(`AI paused (${duration})`);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pause failed");
    } finally { setBusy(null); setShowPauseMenu(false); }
  };

  const doResume = async () => {
    setBusy("resume");
    try {
      const r = await api.post(`/admin/inbox/${conversationId}/ai-resume`);
      setControl(r.data);
      toast.success("AI resumed");
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    } finally { setBusy(null); }
  };

  const doTakeover = async () => {
    setBusy("takeover");
    try {
      const r = await api.post(`/admin/inbox/${conversationId}/ai-takeover`);
      setControl(r.data);
      toast.success("You took control");
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Take control failed");
    } finally { setBusy(null); }
  };

  if (loading && !control) {
    return (
      <div className="px-2 py-1 text-[10px] text-gray-400 flex items-center gap-1" data-testid="ai-control-header-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> AI…
      </div>
    );
  }
  if (!control) return null;

  const mode = control.ai_mode || "hybrid";
  const status = control.ai_status || "active";
  const modeOpt = MODE_OPTIONS.find(m => m.key === mode) || MODE_OPTIONS[2];
  const statusOpt = STATUS_BADGE[status] || STATUS_BADGE.active;
  const StatusIcon = statusOpt.Icon;
  const ModeIcon = modeOpt.Icon;
  const escalation = control.vip_flag || control.complaint_open || control.refund_pending || control.legal_flag || control.payment_dispute;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="ai-control-header">
      {/* Status badge */}
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${statusOpt.color}`} data-testid="ai-control-status">
        <StatusIcon className="w-2.5 h-2.5" /> {statusOpt.label}
        {status === "paused" && countdown && <span className="ml-1 tabular-nums" data-testid="ai-control-countdown">· {countdown}</span>}
      </span>

      {/* Mode dropdown */}
      <div className="relative" ref={modeRef}>
        <button
          onClick={() => setShowModeMenu(s => !s)}
          disabled={busy === "mode"}
          data-testid="ai-control-mode-btn"
          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${modeOpt.color} hover:opacity-90 disabled:opacity-60`}
          title={modeOpt.desc}
        >
          {busy === "mode" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <ModeIcon className="w-2.5 h-2.5" />}
          {modeOpt.label}
          <ChevronDown className="w-2.5 h-2.5" />
        </button>
        {showModeMenu && (
          <div className="absolute right-0 mt-1 z-20 w-60 bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden" data-testid="ai-control-mode-menu">
            {MODE_OPTIONS.map(opt => {
              const Active = opt.Icon;
              return (
                <button
                  key={opt.key}
                  onClick={() => setMode(opt.key)}
                  data-testid={`ai-control-mode-opt-${opt.key}`}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start gap-2 ${opt.key === mode ? "bg-gray-50" : ""}`}
                >
                  <Active className="w-3.5 h-3.5 mt-0.5 text-gray-600" />
                  <div>
                    <div className="text-[11px] font-bold text-[#1a3c5e]">{opt.label}{opt.key === mode && <span className="text-emerald-600 ml-1">·current</span>}</div>
                    <div className="text-[10px] text-gray-500">{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pause / Resume / Takeover quick actions */}
      {status === "active" && mode !== "off" && (
        <div className="relative" ref={pauseRef}>
          <button
            onClick={() => setShowPauseMenu(s => !s)}
            disabled={busy === "pause"}
            data-testid="ai-control-pause-btn"
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-60"
            title="Pause AI temporarily"
          >
            {busy === "pause" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Pause className="w-2.5 h-2.5" />} Pause
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
          {showPauseMenu && (
            <div className="absolute right-0 mt-1 z-20 w-44 bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden" data-testid="ai-control-pause-menu">
              {PAUSE_OPTIONS.map(p => (
                <button
                  key={p.key}
                  onClick={() => doPause(p.key)}
                  data-testid={`ai-control-pause-opt-${p.key}`}
                  className="w-full text-left px-3 py-1.5 text-[11px] font-semibold text-[#1a3c5e] hover:bg-gray-50 inline-flex items-center gap-1.5"
                >
                  <ClockIcon className="w-3 h-3 text-amber-600" /> {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {status !== "active" && mode !== "off" && (
        <button
          onClick={doResume}
          disabled={busy === "resume"}
          data-testid="ai-control-resume-btn"
          className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 text-emerald-800 disabled:opacity-60"
        >
          {busy === "resume" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Play className="w-2.5 h-2.5" />} Resume AI
        </button>
      )}
      {status === "active" && mode !== "off" && (
        <button
          onClick={doTakeover}
          disabled={busy === "takeover"}
          data-testid="ai-control-takeover-btn"
          className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          title="Take human control — AI stops auto-replying"
        >
          {busy === "takeover" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <UserCheck className="w-2.5 h-2.5" />} Take Control
        </button>
      )}

      {/* Escalation flag indicator */}
      {escalation && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300"
          title="Escalation flag is active — AI auto-reply blocked"
          data-testid="ai-control-escalation-flag"
        >
          <Shield className="w-2.5 h-2.5" /> Escalation
        </span>
      )}
    </div>
  );
}

export { MODE_OPTIONS as AI_MODE_OPTIONS };
